import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, SITE } from "../lib/site";
import { Button } from "./ui/button";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-nav"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf9f6]/85 border-b border-[#5fa292]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-24">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center group"
          >
            <img
              src={SITE.logoSage}
              alt="Aima Women's Health"
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) =>
              l.label === "Services" ? (
                <div key={l.to} className="relative group">
                  <NavLink
                    to={l.to}
                    data-testid="nav-link-services"
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-sm tracking-wide transition-colors ${
                        isActive || location.pathname === "/health-concierge"
                          ? "text-[#5fa292] font-medium"
                          : "text-[#3d3d3d] hover:text-[#333]"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      className="transition-transform duration-300 group-hover:rotate-180"
                    />
                  </NavLink>
                  <div
                    data-testid="services-dropdown"
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300"
                  >
                    <Link
                      to="/health-concierge"
                      data-testid="nav-link-health-concierge"
                      className="block whitespace-nowrap bg-white border border-[#e5e0d8] rounded-2xl px-6 py-4 shadow-xl shadow-[#5fa292]/10 hover:border-[#5fa292]/40 transition-colors"
                    >
                      <span className="block text-sm font-medium text-[#333]">
                        Health Concierge
                      </span>
                      <span className="block text-xs text-[#5fa292] mt-1">
                        Six months of 1-1 support
                      </span>
                    </Link>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-colors ${
                      isActive
                        ? "text-[#5fa292] font-medium"
                        : "text-[#3d3d3d] hover:text-[#333]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact" data-testid="nav-cta-book">
              <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-6 py-2 h-auto text-sm font-normal tracking-wide">
                Book a call
              </Button>
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#333] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-[#5fa292]/10 py-6 space-y-4"
          >
            {NAV_LINKS.map((l) => (
              <div key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block text-base ${
                      isActive ? "text-[#5fa292] font-medium" : "text-[#3d3d3d]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
                {l.label === "Services" && (
                  <NavLink
                    to="/health-concierge"
                    data-testid="mobile-nav-link-health-concierge"
                    className={({ isActive }) =>
                      `block text-base pl-4 mt-3 ${
                        isActive ? "text-[#5fa292] font-medium" : "text-[#3d3d3d]"
                      }`
                    }
                  >
                    ↳ Health Concierge
                  </NavLink>
                )}
              </div>
            ))}
            <Link to="/contact" data-testid="mobile-cta-book">
              <Button className="w-full bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full mt-4">
                Book a call
              </Button>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
